import { Calendar as CalendarIcon, X } from 'lucide-react';
import { useState } from 'react';
import { cn, formatDate } from '../../lib/utils';
import { Calendar } from './calendar';
import { Popover, PopoverContent, PopoverTrigger } from './popover';

export function DatePicker({
	value,
	onChange,
	placeholder = 'Pick a date',
	variant = 'default',
	minDate,
	clearable = false,
}: {
	value?: Date | null;
	onChange: (date: Date | undefined) => void;
	placeholder?: string;
	/** "default" for forms (taller, matches Input), "compact" for filter panels */
	variant?: 'default' | 'compact';
	/** Disable all dates before this date */
	minDate?: Date;
	/** Show an X to reset the value back to empty once a date is set */
	clearable?: boolean;
}) {
	const [open, setOpen] = useState(false);
	const showClear = clearable && !!value;

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<div className="relative w-full">
				<PopoverTrigger
					className={cn(
						'flex w-full cursor-pointer items-center gap-2 outline-none transition-colors',
						variant === 'compact'
							? 'h-7 rounded-lg border border-line-2 bg-surface-input px-2.5 text-xs hover:border-line-3'
							: 'h-8 rounded-lg border border-line-2 bg-surface-input px-2.5 text-sm hover:bg-surface-input/50',
						value ? 'text-text-2' : 'text-text-4',
						showClear && 'pr-8'
					)}
				>
					<CalendarIcon size={12} className="shrink-0 text-text-4" />
					{value ? formatDate(value) : placeholder}
				</PopoverTrigger>
				{showClear && (
					<button
						type="button"
						onClick={(e) => {
							e.stopPropagation();
							onChange(undefined);
						}}
						aria-label="Clear date"
						className="absolute right-1.5 top-1/2 flex h-5 w-5 -translate-y-1/2 cursor-pointer items-center justify-center rounded text-text-4 transition-colors hover:bg-surface-hover hover:text-text-2"
					>
						<X size={12} />
					</button>
				)}
			</div>
			<PopoverContent align="start" className="w-auto p-0">
				<Calendar
					mode="single"
					selected={value ?? undefined}
					onSelect={(date) => {
						onChange(date);
						setOpen(false);
					}}
					disabled={minDate ? { before: minDate } : undefined}
				/>
			</PopoverContent>
		</Popover>
	);
}
